/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setUnitId } from '@/lib/slices/unit-slice';
import { setSubunitId } from '@/lib/slices/subunit-slice';
import { setActivityIndex } from '@/lib/slices/activity-slice';

import CourseUpdateService from '@/services/course/course-update-service';
import ActivityEditService from '@/services/course/activity-edit-service';
import { useUnitContentManager } from '@/common/hooks/useUnitContentManager';

import { ActivityData, SubunitData } from 'course-types';
import {
  ActivityRow,
  SubunitRow,
  DraggableCourseContent,
  AddContentButton,
} from '@/common/components/course';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { setActiveCourseData } from '@/lib/slices/course-slice';

interface SubunitTableProps {
  unitId: string;
  unitName: string;
  subunits: { [id: string]: SubunitData };
  activities: Array<ActivityData>;
  isAdminView?: boolean;
  isEditable?: boolean;
  onContentAdded?: () => void;
  onClick?: (
    activity: ActivityData,
    subunit?: SubunitData & { id: string },
    unitId?: string,
  ) => void;
}

export default function SubunitTable({
  subunits,
  activities,
  unitId,
  unitName,
  onClick,
  isAdminView,
  isEditable,
  onContentAdded,
}: SubunitTableProps) {
  const router = useRouter();
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const activeCourse = useAppSelector(() => store.getState().course.activeCourseData);

  // State to track which subunits are expanded
  const [expandedSubunits, setExpandedSubunits] = useState<Set<string>>(new Set());

  // Track which subunit is being edited
  const [editingSubunitId, setEditingSubunitId] = useState<string | null>(null);

  // Drag and drop sensors
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  // Use custom hook for drag-and-drop management
  const {
    localSortedContents,
    localSubunitActivities,
    activeUnitContentDragId,
    handleDragEnd,
    handleSubunitActivityDragEnd,
    handleDragStart,
    handleDragCancel,
  } = useUnitContentManager({ unitId, subunits, activities });

  // Disable delete for top-level unit content when there's only one item total
  const isFinalUnitItem = (localSortedContents ?? []).length <= 1;

  // Toggle subunit expansion
  const toggleSubunit = (subunitId: string) => {
    const newExpanded = new Set(expandedSubunits);
    if (newExpanded.has(subunitId)) {
      newExpanded.delete(subunitId);
    } else {
      newExpanded.add(subunitId);
    }
    setExpandedSubunits(newExpanded);
  };

  // Subunit editing handlers
  const handleSubunitEdit = async (subunitId: string, newName: string) => {
    const result = await CourseUpdateService.updateSubunit(subunitId, newName);
    if (result.success) {
      const updatedCourse = CourseUpdateService.buildUpdatedCourseWithSubunitName(
        activeCourse,
        unitId,
        subunitId,
        newName,
      );
      dispatch(setActiveCourseData(updatedCourse));
    }
  };

  const handleSubunitDelete = (subunitId: string, unitId: string) => {
    dispatch(setSubunitId(subunitId));
    dispatch(setUnitId(unitId));
    dispatch(setModalVisible(true));
    dispatch(setModalName('DeleteSubunit'));
  };

  // Open modal for adding new content/activity to subunit
  const onAddContent = (subunitId: string) => {
    const subunit = subunits[subunitId];
    if (!subunit) return;

    dispatch(setSubunitId(subunitId));
    dispatch(setUnitId(unitId));

    // Sets index to length of current activity array => should be pushed on to end in save content
    dispatch(setActivityIndex(subunit.activities.length));

    dispatch(setModalVisible(true));
    dispatch(setModalName('AddNewContent'));
  };

  // Activity editing - delegates to service
  const handleActivityEdit = (activity: ActivityData, subunitId?: string) => {
    ActivityEditService.openActivityEditor(activity, unitId, subunitId, dispatch, router);
  };

  // Helper to determine if item is an activity
  const isActivityType = (item: any) =>
    ['document', 'video', 'quiz', 'resource', 'assignment'].includes(item?.type);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext
        items={localSortedContents.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className='flex flex-col gap-4'>
          {localSortedContents.map((item) =>
            isActivityType(item) ? (
              <DraggableCourseContent
                key={item.id}
                id={item.id}
                isDisabled={!(isAdminView && isEditable)}
                hideOnDrag
              >
                <ActivityRow
                  activity={item as ActivityData}
                  unitId={unitId}
                  unitName={unitName}
                  courseName={activeCourse.name}
                  courseId={activeCourse.id}
                  isAdminView={isAdminView}
                  isEditable={isEditable}
                  onClick={onClick}
                  onEdit={handleActivityEdit}
                  disableDelete={isFinalUnitItem}
                />
              </DraggableCourseContent>
            ) : (
              <DraggableCourseContent
                key={item.id}
                id={item.id}
                isDisabled={!(isAdminView && isEditable)}
              >
                <SubunitRow
                  subunit={item as SubunitData & { id: string }}
                  unitId={unitId}
                  activeCourseId={activeCourse.id}
                  isExpanded={expandedSubunits.has(item.id)}
                  isAdminView={isAdminView}
                  isEditable={isEditable}
                  isEditing={editingSubunitId === item.id}
                  onToggle={toggleSubunit}
                  onEdit={handleSubunitEdit}
                  onEditStart={(id) => setEditingSubunitId(id)}
                  onEditEnd={() => setEditingSubunitId(null)}
                  onDelete={handleSubunitDelete}
                  disableDelete={isFinalUnitItem}
                >
                  <div className='flex flex-col w-full pl-10 pr-4 gap-2'>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => handleSubunitActivityDragEnd(event, item.id)}
                      modifiers={[restrictToParentElement]}
                    >
                      <SortableContext
                        items={(
                          (localSubunitActivities[item.id] ??
                            (item as SubunitData).activities ??
                            []) as ActivityData[]
                        ).map((activity) => activity.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {(
                          (localSubunitActivities[item.id] ??
                            (item as SubunitData).activities ??
                            []) as ActivityData[]
                        ).map((activity) => (
                          <DraggableCourseContent
                            key={activity.id}
                            id={activity.id}
                            isDisabled={!(isAdminView && isEditable)}
                            dragOpacity={0.5}
                          >
                            <ActivityRow
                              activity={activity}
                              unitId={unitId}
                              unitName={unitName}
                              courseName={activeCourse.name}
                              courseId={activeCourse.id}
                              subunit={item as SubunitData & { id: string }}
                              isAdminView={isAdminView}
                              isEditable={isEditable}
                              onClick={onClick}
                              onEdit={handleActivityEdit}
                            />
                          </DraggableCourseContent>
                        ))}
                      </SortableContext>
                    </DndContext>
                    {isAdminView && isEditable && (
                      <AddContentButton
                        text='Add Subunit Content Item'
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddContent(item.id);
                        }}
                        fullWidth
                      />
                    )}
                  </div>
                </SubunitRow>
              </DraggableCourseContent>
            ),
          )}
        </div>
      </SortableContext>
      <DragOverlay adjustScale={false} dropAnimation={null}>
        {(() => {
          const active = (localSortedContents || []).find((i) => i.id === activeUnitContentDragId);
          if (active && isActivityType(active)) {
            return (
              <div className='flex flex-col gap-2 pointer-events-none'>
                <ActivityRow
                  activity={active as ActivityData}
                  unitId={unitId}
                  unitName={unitName}
                  courseName={activeCourse.name}
                  courseId={activeCourse.id}
                  isAdminView={isAdminView}
                  isEditable={isEditable}
                  onClick={onClick}
                  onEdit={handleActivityEdit}
                />
              </div>
            );
          }
          return null;
        })()}
      </DragOverlay>
    </DndContext>
  );
}
